import React, { useState } from "react";
import { ActionIcon, Box, Group, Stack, Tooltip } from "@mantine/core";
import { useObjectStore } from "../zustand/objects";
import { AddCommentButton } from "./AddCommentButton";
import { ImageButton } from "./FloorColourButton";
import AddModelButton from "./AddModelButton";
import { TemplateDropdown } from "./TemplateDropdown";
import { IconFileUpload, IconSparkles, IconPalette, IconTypography } from '@tabler/icons-react';
import { isAiTextTo3dEnabled } from "../config/aiTextTo3d";
import { chromeActionIconStyles, getEditorChromeTheme } from "../utils/editorChromeTheme";
import { CHROME_ICON_HIT, CONTROL_RADIUS } from "../utils/uiChromeTokens";

const LEFT_NAV_HIT = CHROME_ICON_HIT;
const LEFT_NAV_ICON_SIZE = 17;

function LeftNavIcon({ label, onClick, iconColour, chromeTheme, children }) {
  const [hover, setHover] = useState(false);
  return (
    <Tooltip label={label}>
      <ActionIcon
        size={LEFT_NAV_HIT}
        radius={CONTROL_RADIUS}
        variant="transparent"
        color={!hover ? iconColour.normal : iconColour.hover}
        onPointerOver={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        onClick={onClick}
        style={chromeActionIconStyles(hover, chromeTheme)}
      >
        {children}
      </ActionIcon>
    </Tooltip>
  );
}

const GenerateObject = ({ setAiOpen, iconColour, chromeTheme }) => {
  const aiEnabled = isAiTextTo3dEnabled();
  return (
    <Tooltip
      label={
        aiEnabled
          ? "Generate model with AI"
          : "AI models — coming soon on paid plans"
      }
    >
      <ActionIcon
        size={LEFT_NAV_HIT}
        radius={CONTROL_RADIUS}
        variant="transparent"
        onClick={setAiOpen}
        style={chromeActionIconStyles(false, chromeTheme)}
        onPointerOver={(e) => {
          Object.assign(e.currentTarget.style, chromeActionIconStyles(true, chromeTheme));
        }}
        onPointerLeave={(e) => {
          Object.assign(e.currentTarget.style, chromeActionIconStyles(false, chromeTheme));
        }}
      >
        <IconSparkles size={LEFT_NAV_ICON_SIZE} color={iconColour.normal} />
      </ActionIcon>
    </Tooltip>
  );
};

const UploadObject = ({ setUploadOpen, iconColour, chromeTheme }) => {
  const { setImagePurpose } = useObjectStore();

  const handleClick = () => {
    setUploadOpen();
    setImagePurpose("object");
  };

  return (
    <LeftNavIcon label="Uploads" onClick={handleClick} iconColour={iconColour} chromeTheme={chromeTheme}>
      <IconFileUpload size={LEFT_NAV_ICON_SIZE} />
    </LeftNavIcon>
  );
};

const FigmaObject = ({ iconColour, chromeTheme }) => {
  const { setImagePurpose, switchImageModalOpen } = useObjectStore();

  const handleClick = () => {
    setImagePurpose("figma");
    switchImageModalOpen();
  };

  return (
    <LeftNavIcon label="Embed a Figma frame" onClick={handleClick} iconColour={iconColour} chromeTheme={chromeTheme}>
      <IconPalette size={LEFT_NAV_ICON_SIZE} />
    </LeftNavIcon>
  );
};

const AddTextObject = ({ iconColour, chromeTheme }) => {
  const { switchTextModalOpen } = useObjectStore();

  return (
    <LeftNavIcon
      label="Add text"
      onClick={() => switchTextModalOpen()}
      iconColour={iconColour}
      chromeTheme={chromeTheme}
    >
      <IconTypography size={LEFT_NAV_ICON_SIZE} />
    </LeftNavIcon>
  );
};

const EditorLeftNavbar = ({ projectKey, iconColour, setAiOpen, setUploadOpen, isDark = false }) => {
  const chromeTheme = getEditorChromeTheme(isDark);
  const dividerColor = iconColour?.divider || chromeTheme.border;

  return (
    <Stack
      gap={8}
      justify="flex-start"
      align="center"
      style={{
        width: LEFT_NAV_HIT,
        marginLeft: "auto",
        marginRight: "auto",
        paddingTop: 12,
        paddingBottom: 12,
      }}
    >
      <GenerateObject setAiOpen={setAiOpen} iconColour={iconColour} chromeTheme={chromeTheme} />
      <FigmaObject iconColour={iconColour} chromeTheme={chromeTheme} />
      <Box style={{ width: 24, borderTop: `1px solid ${dividerColor}`, marginTop: 2, marginBottom: 2 }} />
      <UploadObject setUploadOpen={setUploadOpen} iconColour={iconColour} chromeTheme={chromeTheme} />
      <AddTextObject iconColour={iconColour} chromeTheme={chromeTheme} />
      <Box style={{ width: 24, borderTop: `1px solid ${dividerColor}`, marginTop: 2, marginBottom: 2 }} />
      <ImageButton iconColour={iconColour} leftNav />
      <AddModelButton iconColour={iconColour} leftNav />
      <TemplateDropdown projectKey={projectKey} iconColour={iconColour} leftNav />
      <Group>
        <AddCommentButton leftNav iconColour={iconColour} />
      </Group>
    </Stack>
  );
};

export default EditorLeftNavbar;
